import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import { KpiDelta } from '../../components/molecules/KpiTile';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../components/foundations/Icon';
import { TRANSLATION_KEY as WATCHLIST_TRANSLATION_KEY } from '../../watchlist/constants';
import { buildVerdictDelta } from '../../watchlist/verdictFormat';
import { GROUPED_TOAST_MAX_ROWS, TRANSLATION_KEY } from '../constants';
import { alertShape } from '../propShapes';
import {
    alertBodyKey,
    alertBodyText,
    alertHeadlineMetric,
    alertMetrics,
    isLongSummary,
    latestAlertTime,
    needsAttention,
} from '../utils';
import AlertMetrics from './AlertMetrics';
import AlertTime from './AlertTime';
import MonitorStillChecking from './MonitorStillChecking';
import css from './MonitorGroupedToast.css';

const TOAST_KEY = `${TRANSLATION_KEY}.TOASTS`;

// Unmounted on collapse, so the read-more toggle starts closed again on the next open.
const GroupedRowDetail = ({ id, alert, onOpenThread }) => {
    const [showFull, setShowFull] = useState(false);
    const body = alertBodyText(alert, showFull);
    // error/no_verdict carry no headline, so without this the row opens onto nothing.
    const bodyKey = body ? null : alertBodyKey(alert);
    const clampable = isLongSummary(alert);
    const metrics = alertMetrics(alert);

    return (
        <div className={css.detail} id={id}>
            {body && <div className={css.detailBody}>{body}</div>}
            {bodyKey && (
                <div className={css.detailBody}>
                    <Translate id={`${WATCHLIST_TRANSLATION_KEY}.CARD.${bodyKey}`} />
                </div>
            )}
            {clampable && (
                <button
                    type="button"
                    className={css.expandToggle}
                    aria-expanded={showFull}
                    onClick={() => setShowFull(!showFull)}
                >
                    <Translate id={`${TOAST_KEY}.${showFull ? 'SHOW_LESS' : 'READ_FULL'}`} />
                </button>
            )}
            {metrics && <AlertMetrics items={metrics} />}
            <div className={css.detailActions}>
                <button type="button" className={css.actionButton} onClick={onOpenThread}>
                    <Translate id={`${TOAST_KEY}.OPEN_THREAD`} />
                </button>
            </div>
        </div>
    );
};

GroupedRowDetail.propTypes = {
    id: PropTypes.string.isRequired,
    alert: alertShape.isRequired,
    onOpenThread: PropTypes.func.isRequired,
};

const GroupedRow = ({ alert, expanded, onToggle, onOpenThread }) => {
    const metric = alertHeadlineMetric(alert);
    const delta = metric ? buildVerdictDelta(metric.delta, metric.tone) : null;
    const muted = !needsAttention(alert);
    const detailId = `monitor-alert-${alert.monitorId}`;
    return (
        <div className={css.rowWrap}>
            <button
                type="button"
                className={css.row}
                aria-expanded={expanded}
                aria-controls={expanded ? detailId : undefined}
                onClick={onToggle}
            >
                <span className={classNames(css.dot, { [css.dotMuted]: muted })} />
                <span className={classNames(css.rowTitle, { [css.rowTitleMuted]: muted })} title={alert.title || undefined}>
                    {alert.title || <Translate id={`${WATCHLIST_TRANSLATION_KEY}.CARD.UNTITLED`} />}
                </span>
                {delta && (
                    <span className={css.rowMetric}>
                        {/* label, not field: field is the raw report column ("adn_cost"). */}
                        <span className={css.rowMetricLabel} title={metric.label}>
                            {metric.label}
                        </span>
                        <KpiDelta delta={delta} />
                    </span>
                )}
                <Icon
                    type={IconTypes.TABLER}
                    name={expanded ? 'chevron-up' : 'chevron-down'}
                    size={IconSizes.XX_SMALL}
                    variant={IconVariants.LIGHT}
                    interactive={false}
                    className={css.chevron}
                />
            </button>
            {expanded && <GroupedRowDetail id={detailId} alert={alert} onOpenThread={onOpenThread} />}
        </div>
    );
};

GroupedRow.propTypes = {
    alert: alertShape.isRequired,
    expanded: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    onOpenThread: PropTypes.func.isRequired,
};

const MonitorGroupedToast = ({
    alerts,
    checkingCount,
    translate,
    onDismiss,
    onMarkAllRead,
    onOpen,
    onOpenAlert,
    onExpandAlert,
}) => {
    const [expandedId, setExpandedId] = useState(null);
    const visible = alerts.slice(0, GROUPED_TOAST_MAX_ROWS);
    // An open row is what the user is reading, so a newer result adds a row rather than
    // pushing it off the card mid-read.
    const openBelow =
        expandedId && !visible.some(alert => alert.monitorId === expandedId)
            ? alerts.find(alert => alert.monitorId === expandedId)
            : null;
    const shown = openBelow ? [...visible, openBelow] : visible;
    const shownIds = new Set(shown.map(alert => alert.monitorId));
    const hidden = alerts.filter(alert => !shownIds.has(alert.monitorId));
    const attention = alerts.some(needsAttention);
    const overflowKey = hidden.some(needsAttention) ? 'GROUPED_OVERFLOW_ATTENTION' : 'GROUPED_OVERFLOW';

    // One row at a time: three open rows is taller than the viewport the card sits in.
    const toggle = alert => {
        const opening = expandedId !== alert.monitorId;
        setExpandedId(opening ? alert.monitorId : null);
        if (opening) onExpandAlert(alert);
    };

    return (
        <div className={css.card}>
            <div className={classNames(css.accent, { [css.accentAttention]: attention })} />
            <div className={css.content}>
                <div className={css.header}>
                    <span className={css.headerTitle}>
                        <Translate id={`${TOAST_KEY}.GROUPED_TITLE`} data={{ count: alerts.length }} />
                    </span>
                    <span className={css.headerTime}>
                        <AlertTime lastRunAt={latestAlertTime(alerts)} />
                    </span>
                    <span className={css.spacer} />
                    <button
                        type="button"
                        className={css.closeButton}
                        aria-label={translate(`${TOAST_KEY}.DISMISS`)}
                        onClick={onDismiss}
                    >
                        <Icon
                            type={IconTypes.TABLER}
                            name="x"
                            size={IconSizes.XX_SMALL}
                            variant={IconVariants.LIGHT}
                            interactive={false}
                        />
                    </button>
                </div>

                <div className={css.rows}>
                    {shown.map(alert => (
                        <GroupedRow
                            key={alert.monitorId}
                            alert={alert}
                            expanded={expandedId === alert.monitorId}
                            onToggle={() => toggle(alert)}
                            onOpenThread={() => onOpenAlert(alert)}
                        />
                    ))}
                    {hidden.length > 0 && (
                        <button type="button" className={css.overflow} onClick={onOpen}>
                            <Translate id={`${TOAST_KEY}.${overflowKey}`} data={{ count: hidden.length }} />
                        </button>
                    )}
                </div>

                <div className={css.actions}>
                    <button type="button" className={css.actionButton} onClick={onOpen}>
                        <Translate id={`${TOAST_KEY}.REVIEW_IN_WATCHLIST`} />
                    </button>
                    <span className={css.spacer} />
                    <button type="button" className={css.markAllButton} onClick={onMarkAllRead}>
                        <Translate id={`${TOAST_KEY}.MARK_ALL_READ`} />
                    </button>
                </div>
            </div>
            <MonitorStillChecking count={checkingCount} />
        </div>
    );
};

MonitorGroupedToast.propTypes = {
    alerts: PropTypes.arrayOf(alertShape).isRequired,
    checkingCount: PropTypes.number.isRequired,
    translate: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onMarkAllRead: PropTypes.func.isRequired,
    onOpen: PropTypes.func.isRequired,
    onOpenAlert: PropTypes.func.isRequired,
    onExpandAlert: PropTypes.func.isRequired,
};

export default withLocalize(MonitorGroupedToast);
